import React, { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { NOTIF_META, fmtDate } from "@/lib/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(n.data);
      setCount(c.data.count);
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openNotification = async (note) => {
    if (!note.read) {
      await api.post(`/notifications/${note.id}/read`).catch(() => {});
    }
    navigate(`/orders/${note.order_id}?section=${note.section}`);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative flex h-11 w-11 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100"
        >
          <Bell className="h-6 w-6" />
          {count > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-xs font-bold text-white"
            >
              {count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <span className="font-heading text-base font-bold text-slate-900">Notifications</span>
          <button
            data-testid="mark-all-read-btn"
            onClick={markAll}
            className="text-sm font-semibold text-blue-600 hover:underline"
          >
            Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto mf-scrollbar">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">You're all caught up.</div>
          )}
          {items.map((n) => {
            const meta = NOTIF_META[n.type] || NOTIF_META.info;
            return (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => openNotification(n)}
                className={cn(
                  "flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50",
                  !n.read && "bg-blue-50/50"
                )}
              >
                <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", meta.dot)} />
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug text-slate-800">{n.message}</p>
                  <p className="mt-1 text-xs text-slate-400">{fmtDate(n.created_at)}</p>
                </div>
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
